import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMovimentacaoMutations } from '../../../hooks/data';
import type { Movimentacao } from '../../../models';

interface FotosMovimentacaoSectionProps {
  solicitacaoId: string;
  movimentacao: Movimentacao;
  podeEditar: boolean;
}

/**
 * Fotos legadas, no nível da Movimentação (`Movimentacao.fotos`) — de antes de as fotos
 * passarem a ser por equipamento (ver FotosEquipamentoSection). Sem upload novo: só exibe
 * (e deixa remover) o que já existia, e some quando não há nenhuma. Continuam entrando no
 * Termo e contando `etapas.fotos` como concluída (ver calcularEtapaFotos) — o
 * movimentacaoService recalcula a etapa quando `fotos` muda.
 */
export default function FotosMovimentacaoSection({
  solicitacaoId,
  movimentacao,
  podeEditar,
}: FotosMovimentacaoSectionProps) {
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);

  const fotos = movimentacao.fotos ?? [];
  if (fotos.length === 0) return null;

  const handleRemover = (url: string) => {
    atualizarMovimentacao.mutate({
      id: movimentacao.id,
      fotos: fotos.filter((f) => f !== url),
    });
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <p className="text-xs font-medium uppercase text-gray-400">Fotos anteriores</p>
      <p className="mt-0.5 text-xs text-gray-500">
        Anexadas antes de as fotos ficarem por equipamento. Continuam entrando no termo.
      </p>

      <div className="mt-2 flex flex-wrap gap-2">
        {fotos.map((url) => (
          <div key={url} className="relative">
            <a href={url} target="_blank" rel="noreferrer">
              <img src={url} alt="Foto anterior da movimentação" className="h-16 w-16 rounded-md object-cover" />
            </a>
            {podeEditar && (
              <button
                type="button"
                className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-gray-500 shadow hover:text-red-600"
                onClick={() => handleRemover(url)}
                title="Remover foto"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
